/*    
<summary>
  This functional component "AddCategoryManagementForm" provides form to add new user or update existing user.
  Developer:Sahil Bharti, Created Date:06-January-2025
</summary>
<returns>Returns JSX</returns>
*/

import {
  Field,
  FieldArray,
  Form,
  Formik,
  FormikValues,
  useFormikContext,
} from "formik";
import { useEffect, useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import LoaderButton from "../../../../shared-components/button/loader-button";
import FormikFormInput from "../../../../shared-components/FormikFormHandlers/FormikFormInput";
import Label from "../../../../shared-components/label/label";
import "./add-update-reservation-settings-forms.css";
import dayjs from "dayjs";
import React from "react";
import FormikFormCheckBox from "../../../../shared-components/FormikFormHandlers/FormikFormCheckBox";
import { formatMessage } from "../../../../translations/format-message";
import weekdayEnum from "../../../../constants/enums/weekday-enum";
import { useHistory, useLocation, useParams } from "react-router-dom";
import _ from "lodash";
import FormikHourPicker from "../../../../shared-components/FormikFormHandlers/FormikHourPickerMUI";
import FormikFormTimePicker from "../../../../shared-components/FormikFormHandlers/FormikFormTimePicker";
import { ReservationSettingSchema } from "../../../../shared-components/FormikFormHandlers/FormikValidationSchema";
import { IOption } from "../../../../models/ICommon";
import FormikFormSelect from "../../../../shared-components/FormikFormHandlers/FormikFormSelect";
import RoutesConstants from "../../../routes.constant";
import { weekdayShortEnum } from "../../../../constants/enums/weekday-short-enum";
import userTypeEnum from "../../../../constants/enums/user-type-enum";
import {
  getUserTypeFromLocalStorage,
  getStoreIdFromLocalStorage,
} from "../../../../helpers/localstorage-helper";
import FormikFormInputForRegexCheck from "../../../../shared-components/FormikFormHandlers/FormikFormInputForRegexCheck";
import { integerRegex } from "../../../../constants/constants";
import { toHalfWidth } from "../../../../helpers/validate-helper";
import { minuteIntervalOptions } from "../../../../constants/options.constant";

interface IProps {
  id: number;
  isLoading: boolean;
  submitHandler: (data: any) => void;
  onModalClosed: () => void;
  initialValues: any;
  timeOptions: IOption[];
  storeOptions: IOption[];
  disableAction: boolean;
}

interface BusinessVM {
  WeekDays: number;
  IsClosed: boolean;
  OpenTime: string;
  ClosedTime: string;
}
const AddUpdateReservationSettingForm = (props: IProps) => {
  const history = useHistory();
  const location = useLocation<{ storeId?: number }>();
  let StoreId = location.state?.storeId ?? 0;
  const userType = getUserTypeFromLocalStorage();
  if (userType === userTypeEnum[userTypeEnum.Store]) {
    StoreId = getStoreIdFromLocalStorage() || 0;
  }
  const selectedStore = props.storeOptions.find(
    (store) => store.id === StoreId
  );
  const storeName = selectedStore ? selectedStore.value : "Unknown Store";

  const validateTime = (
    value: { $d?: Date } | null,
    allValues: {
      BusinessSettings: {
        OpenTime: string;
        ClosedTime: string;
        IsClosed: boolean;
      }[];
    },
    index: number
  ): string | undefined => {
    if (allValues.BusinessSettings[index]?.IsClosed) {
      return undefined;
    }

    const openTime = allValues.BusinessSettings[index]?.OpenTime;
    const closedTime = allValues.BusinessSettings[index]?.ClosedTime;

    if (
      !openTime ||
      !closedTime ||
      openTime === "00:00" ||
      closedTime === "00:00"
    ) {
      return "field_required";
    }

    if (openTime === closedTime) {
      return "opening_and_closing_time_cannot_be_same";
    }

    if (closedTime < openTime) {
      return "closing_time_cannot_be_earlier_than_opening_time";
    }

    return undefined;
  };

 const ReservationTimeObserver = ({ storeId }: { storeId: number }) => {
  const { values, setFieldValue, resetForm } = useFormikContext<any>();
  const prevStoreIdRef = React.useRef(storeId);

  useEffect(() => {
    if (prevStoreIdRef.current !== storeId) {
      resetForm(); // reset only if StoreId really changed
      prevStoreIdRef.current = storeId;
    }
  }, [storeId, resetForm]);

  useEffect(() => {
    if (Array.isArray(values.BusinessSettings)) {
      values.BusinessSettings.forEach((setting: any, index: number) => {
        if (setting.IsClosed) {
          if (
            setting.OpenTime !== "00:00" ||
            setting.ClosedTime !== "00:00"
          ) {
            setFieldValue(`BusinessSettings.${index}.OpenTime`, "00:00");
            setFieldValue(`BusinessSettings.${index}.ClosedTime`, "00:00");
          }
        }
      });
    }
  }, [values.BusinessSettings, setFieldValue]);

  return null;
};

  const stableInitialValues = useMemo(() => props.initialValues, [props.id]);
  
  return (
    <>
      <Formik
        initialValues={stableInitialValues}
        enableReinitialize
        validationSchema={ReservationSettingSchema}
        validateOnBlur={true}
        validateOnChange={true}
        onSubmit={async (values, { setSubmitting }) => {
          const formattedBusinessSettings = values.BusinessSettings.map(
            (setting: BusinessVM) => ({
              StoreId: StoreId,
              WeekDays: setting.WeekDays,
              IsClosed: setting.IsClosed,
              OpenTime: setting.OpenTime
                ? dayjs(setting.OpenTime, "HH:mm").format("HH:mm:ss")
                : "00:00:00",
              ClosedTime: setting.ClosedTime
                ? dayjs(setting.ClosedTime, "HH:mm").format("HH:mm:ss")
                : "00:00:00",
            })
          );

          formattedBusinessSettings.push({
            StoreId: StoreId,
            WeekDays: 8,
            IsClosed: values?.IsHoliday,
            OpenTime: "00:00:00",
            ClosedTime: "00:00:00",
          });

          const newObj = {
            StoreId: StoreId,
            CancelationTime: values.CancelationTime
              ? Number(toHalfWidth(values.CancelationTime))
              : null,
            NewReservationTime: values.NewReservationTime
              ? Number(toHalfWidth(values.NewReservationTime))
              : null,
            CalendarIntervalTime: Number(values.CalendarIntervalTime),
            IntervalTime: values.IntervalTime
              ? Number(values.IntervalTime) === -1
                ? null
                : Number(values.IntervalTime)
              : null,
            SeatAvailable: values.SeatAvailable
              ? Number(toHalfWidth(values.SeatAvailable))
              : null,
            Precaution: values.Precaution,
            EmailNotifyToStore: values.EmailNotifyToStore,
            BusinessSettings: formattedBusinessSettings,
          };

          const isUpdateMode = props.id !== undefined && props.id > 0;

          // if (_.isEqual(props.initialValues, values)) {
          //   setSubmitting(false);
          //   return;
          // }

          const finalObj = isUpdateMode
            ? { ...newObj, IsUpdated: true }
            : newObj;

          props.submitHandler(finalObj);
        }}
      >
        {({ setFieldValue, values, resetForm }) => (
          <Form className="">
            <ReservationTimeObserver storeId={StoreId} />
            <div className="row ms-0 me-0">
              <div className="col-xxl-3 col-lg-4 col-md-12 col-12">
                <div className="border-bottom pb-3 text-left">
                  <FormattedMessage id="reservation_settings" /> ({storeName})
                </div>

                <div className="mt-2 row g-3">
                  <div className="col-lg-12 col-md-4 col-sm-6 col-6">
                    <Label
                      className="label-style"
                      required={true}
                      label="cancellation"
                    />
                    <Field
                      className="form-control"
                      name="CancelationTime"
                      placeholder="cancellation_time"
                      regex={integerRegex}
                      as={FormikFormInputForRegexCheck}
                    />
                  </div>

                  <div className="col-lg-12 col-md-4 col-sm-6 col-6">
                    <Label
                      className="label-style"
                      required={true}
                      label="new_reservation"
                    />
                    <Field
                      className="form-control"
                      name="NewReservationTime"
                      placeholder="new_reservation_time"
                      regex={integerRegex}
                      as={FormikFormInputForRegexCheck}
                    />
                  </div>

                  <div className="col-lg-12 col-md-4 col-sm-6 col-6">
                    <Label
                      className="label-style"
                      required={true}
                      label="interval_time"
                    />
                    <Field
                      className="form-select"
                      name="IntervalTime"
                      as={FormikFormSelect}
                      options={props.timeOptions}
                    />
                  </div>

                  <div className="col-lg-12 col-md-4 col-sm-6 col-6">
                    <Label
                      className="label-style"
                      required={true}
                      label="calendar_interval_time"
                    />
                    <Field
                      className="form-select"
                      name="CalendarIntervalTime"
                      as={FormikFormSelect}
                      options={minuteIntervalOptions}
                    />
                  </div>

                  <div className="col-lg-12 col-md-4 col-sm-6 col-6">
                    <Label
                      className="label-style"
                      required={true}
                      label="no_of_seats"
                    />
                    <Field
                      className="form-control"
                      name="SeatAvailable"
                      type="number"
                      placeholder="enter_no_of_seat_available"
                      as={FormikFormInput}
                    />
                  </div>

                  <div className="col-lg-12 col-md-4 col-sm-6 col-6">                    
                    <Label
                      className="label-style"
                      required={true}
                      label="precaution"
                    />
                    <Field
                      name="Precaution"
                      as={FormikFormInput}
                      placeholder="precaution"
                      className="form-control"
                    />
                  </div>
                  <div className="form-group col-lg-12 col-12">   
                     <div className="form-check form-switch d-flex align-items-center p-0">
                      <label
                        htmlFor="EmailNotifyToStore"
                        className="form-check-label"
                      >
                        <FormattedMessage id="email_notify_to_store" />
                      </label> 
                      <Field
                        className="form-check-input mt-0 ms-2"
                        name="EmailNotifyToStore"
                        type="checkbox"
                        id="EmailNotifyToStore"
                      />
                    </div>                 
                  </div>
                  
                </div>
              </div>
              <div className="col-xxl-9 col-lg-8 col-md-12 col-12 border-start-0 border-start-lg-1 border-top-lg-0 pt-4 pt-lg-0">
                <div className="border-bottom pb-3 mb-3 mb-lg-0">
                  <FormattedMessage id="business_hours_settings" />
                </div>

                <div className="tableDesign">
                  <table className="table table-bordered border linkbaseout-table">
                    <thead>
                      <tr>
                        <th>
                          <FormattedMessage id="days" />
                        </th>
                        <th>
                          <FormattedMessage id="is_closed" />
                        </th>
                        <th>
                          <FormattedMessage id="opening_hours" />
                        </th>
                      </tr>
                    </thead>
                    <FieldArray name="BusinessSettings">
                      {({ insert, remove, push }) => (
                        <tbody>
                          {values.BusinessSettings?.map(
                            (obj: any, index: number) => (
                              <tr key={index}>
                                <td>
                                  {formatMessage(
                                    weekdayShortEnum[obj.WeekDays]
                                  )}
                                </td>

                                <Field name={`BusinessSettings.${index}`}>
                                  {({ field }: any) => (
                                    <React.Fragment>
                                      <td>
                                        <label>
                                          <Field
                                            data-testid="defaultSettings"
                                            name={`BusinessSettings.${index}.IsClosed`}
                                            disabled = {

                                             (index == 7 && values.IsHoliday == false) ? true :

                                             false
                                            }
                                            type="checkbox"
                                            id="inlineFormCheck"
                                            as={FormikFormCheckBox}
                                          />
                                        </label>
                                      </td>
                                      <td className="row align-items-center">
                                        <div className="col-5">
                                          <Field
                                            name={`BusinessSettings.${index}.OpenTime`}
                                            as={FormikFormTimePicker}
                                            // className="form-control"
                                            disabled={

                                             (index == 7 && values.IsHoliday == false) ? true :

                                              values.BusinessSettings[index]
                                                .IsClosed
                                            }
                                            // validate={(value: any) =>
                                            //   validateTime(value, values, index)
                                            // }
                                          />
                                        </div>
                                        <div className="col-2 text-center">
                                          ~
                                        </div>
                                        <div className="col-5">
                                          <Field
                                            name={`BusinessSettings.${index}.ClosedTime`}
                                            type="time"
                                            // className="form-control"
                                            disabled={

                                             (index == 7 && values.IsHoliday == false) ? true :

                                              values.BusinessSettings[index]
                                                .IsClosed
                                            }
                                            as={FormikFormTimePicker}
                                            // validate={(value: any) =>
                                            //   validateTime(value, values, index)
                                            // }
                                          />
                                        </div>
                                      </td>
                                    </React.Fragment>
                                  )}
                                </Field>
                              </tr>
                            )
                          )}
                          <tr key={8}>
                            <td className="py-4">
                              <Field
                                data-testid="defaultSettings"
                                name={`IsHoliday`}
                                className="me-2"
                                type="checkbox"
                                id="inlineFormCheck"
                                // getChangeValue={
                                //   (e:any) => {
                                //     if(e == false){
                                //       setFieldValue("BusinessSettings.7.IsClosed", true);
                                //     }
                                //   }
                                // }
                                as={FormikFormCheckBox}
                              />
                              <FormattedMessage id="is_holiday" />
                            </td>
                          </tr>
                        </tbody>
                      )}
                    </FieldArray>
                  </table>
                </div>
              </div>
            </div>
            <div className="text-center p-2">
              <Button
                variant="outline-danger"
                type="button"
                className="me-2"
                onClick={() => history.push(RoutesConstants.StoreList)}
                disabled={props.isLoading || props.disableAction}
              >
                <i className="bi bi-x-lg me-1"></i>
                <FormattedMessage id="close" />
              </Button>
              {props.id > 0 ? (
                <LoaderButton
                  type="submit"
                  iconName="bi-check-lg"
                  isLoading={props.isLoading}
                  disabled={props.disableAction}
                  text={"button_update"}
                />
              ) : (
                <LoaderButton
                  type="submit"
                  iconName="bi-check-lg"
                  isLoading={props.isLoading}
                  text={"button_save"}
                  disabled={StoreId === 0 || props.disableAction}
                />
              )}
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default AddUpdateReservationSettingForm;
